import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { mentorshipApi, showError, showSuccess } from "../lib";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft,
  Loader,
  Save,
  Plus,
  Trash2,
  Check,
  Star,
  Users,
  User,
  Calendar,
  Camera,
  Mail,
  Linkedin,
  Globe,
  Twitter,
  Facebook,
  Clock,
  Briefcase,
  GraduationCap,
  MessageCircle,
  DollarSign,
  X,
  Edit2,
  Award,
  TrendingUp,
  Heart,
} from "lucide-react";

interface MentorProfile {
  _id: string;
  user: string;
  firstName?: string;
  lastName?: string;
  name: string;
  email?: string;
  title: string;
  company: string;
  companyColor?: string;
  bio: string;
  expertise: string[];
  pricePerSession: number;
  rating?: number;
  reviewsCount?: number;
  totalSessions: number;
  totalStudents: number;
  isVerified: boolean;
  isTopMentor: boolean;
  verified?: boolean;
  topMentor?: boolean;
  avatar?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  facebook?: string;
  hourlyRate?: number;
  yearsOfExperience?: number;
  languages?: string[];
  responseTime?: string;
  availability?: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }[];
}

interface AvailabilitySlot {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MentorProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<MentorProfile | null>(null);
  const [isNewProfile, setIsNewProfile] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    title: "",
    company: "",
    bio: "",
    expertise: [] as string[],
    pricePerSession: 50,
    linkedin: "",
    twitter: "",
    website: "",
    facebook: "",
    yearsOfExperience: 0,
    languages: [] as string[],
  });
  
  const [newExpertise, setNewExpertise] = useState("");
  const [newLanguage, setNewLanguage] = useState("");
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [newSlot, setNewSlot] = useState<AvailabilitySlot>({
    dayOfWeek: 1,
    startTime: "09:00",
    endTime: "17:00",
  });
  const [activeTab, setActiveTab] = useState<"profile" | "availability" | "social">("profile");

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await mentorshipApi.getMyMentorProfile();
      if (response.success && response.data) {
        setProfile(response.data);
        setFormData({
          firstName: response.data.firstName || "",
          lastName: response.data.lastName || "",
          email: response.data.email || "",
          title: response.data.title || "",
          company: response.data.company || "",
          bio: response.data.bio || "",
          expertise: response.data.expertise || [],
          pricePerSession: response.data.pricePerSession || 50,
          linkedin: response.data.linkedin || "",
          twitter: response.data.twitter || "",
          website: response.data.website || "",
          facebook: response.data.facebook || "",
          yearsOfExperience: response.data.yearsOfExperience || 0,
          languages: response.data.languages || [],
        });
        setAvailability(response.data.availability || []);
      } else {
        setIsNewProfile(true);
      }
    } catch (err: any) {
      const errorMessage = err.message || "";
      if (err.response?.status === 404 || errorMessage.includes("not found") || errorMessage.includes("Not a mentor")) {
        setIsNewProfile(true);
      } else {
        console.error("Failed to fetch profile:", err);
        showError("Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = {
        ...formData,
        availability,
      };
      
      let response;
      if (isNewProfile) {
        response = await mentorshipApi.createMentorProfile(data);
      } else {
        response = await mentorshipApi.updateMentorProfile(data);
      }
      
      if (response.success) {
        showSuccess(isNewProfile ? "Profile created!" : "Profile updated!");
        fetchProfile();
        setIsNewProfile(false);
      }
    } catch (err: any) {
      console.error("Failed to save profile:", err);
      showError(err.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const addExpertise = () => {
    if (newExpertise.trim() && !formData.expertise.includes(newExpertise.trim())) {
      setFormData((prev) => ({
        ...prev,
        expertise: [...prev.expertise, newExpertise.trim()],
      }));
      setNewExpertise("");
    }
  };

  const removeExpertise = (exp: string) => {
    setFormData((prev) => ({
      ...prev,
      expertise: prev.expertise.filter((e) => e !== exp),
    }));
  };

  const addLanguage = () => {
    if (newLanguage.trim() && !formData.languages.includes(newLanguage.trim())) {
      setFormData((prev) => ({
        ...prev,
        languages: [...prev.languages, newLanguage.trim()],
      }));
      setNewLanguage("");
    }
  };

  const removeLanguage = (lang: string) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((l) => l !== lang),
    }));
  };

  const addAvailability = () => {
    if (newSlot.startTime && newSlot.endTime) {
      setAvailability((prev) => [...prev, newSlot]);
      setNewSlot({
        dayOfWeek: 1,
        startTime: "09:00",
        endTime: "17:00",
      });
    }
  };

  const removeAvailability = (index: number) => {
    setAvailability((prev) => prev.filter((_, i) => i !== index));
  };

  const fmtXAF = (amount: number) =>
    new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      maximumFractionDigits: 0,
    }).format(amount);

  if (loading) {
    return (
      <main className="min-h-screen pt-16 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader className="w-10 h-10 mx-auto mb-3 text-primary animate-spin" />
            <p className="text-sm text-gray-500">Loading profile...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pb-20 pt-14 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-4xl px-4 mx-auto">
        {/* Header */}
        <div className="sticky z-10 flex items-center justify-between p-3 mb-4 shadow-sm top-14 bg-white/95 backdrop-blur-sm rounded-xl">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/dashboard/mentor")}
              className="p-2 text-gray-500 transition-colors rounded-lg hover:text-primary hover:bg-orange-50"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base font-bold text-gray-900">
                {isNewProfile ? "Become a Mentor" : "Mentor Profile"}
              </h1>
              <p className="text-[11px] text-gray-500">
                {isNewProfile ? "Set up your profile to start mentoring" : "Manage your mentoring profile"}
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all"
          >
            {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving..." : "Save"}
          </button>
        </div>

        {/* Profile Avatar - Large on Mobile */}
        <div className="p-5 mb-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-3">
              <div className="overflow-hidden rounded-full shadow-lg w-28 h-28 md:w-32 md:h-32 bg-gradient-to-br from-primary/20 to-orange-100 ring-4 ring-white">
                {user?.avatar ? (
                  <img src={user.avatar} alt="Profile" className="object-cover w-full h-full" />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-4xl font-bold md:text-5xl text-primary">
                    {user?.firstName?.[0] || user?.name?.[0] || "U"}
                  </div>
                )}
              </div>
              <button className="absolute flex items-center justify-center w-8 h-8 transition-colors bg-white border border-gray-200 rounded-full shadow-md bottom-1 right-1 hover:bg-gray-50">
                <Camera className="w-3.5 h-3.5 text-gray-500" />
              </button>
            </div>
            <h2 className="text-lg font-bold text-gray-900 md:text-xl">
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}` 
                : user?.name || user?.username || "User"}
            </h2>
            {profile?.email && (
              <div className="flex items-center justify-center gap-1 mt-0.5 text-xs text-gray-500">
                <Mail className="w-3 h-3" />
                {profile.email}
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-1">Profile photo from account</p>
          </div>
        </div>

        {/* Stats Cards - Horizontal Scroll on Mobile */}
        {!isNewProfile && profile && (
          <div className="flex gap-2 px-2 pb-2 mb-4 -mx-2 overflow-x-auto">
            <div className="min-w-[100px] bg-white rounded-xl p-3 border border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-1.5 mb-1">
                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                <span className="text-sm font-bold text-gray-900">{profile.rating?.toFixed(1) || "New"}</span>
              </div>
              <p className="text-[10px] text-gray-500">Rating</p>
            </div>
            <div className="min-w-[100px] bg-white rounded-xl p-3 border border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-1.5 mb-1">
                <Users className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-sm font-bold text-gray-900">{profile.totalStudents || 0}</span>
              </div>
              <p className="text-[10px] text-gray-500">Students</p>
            </div>
            <div className="min-w-[100px] bg-white rounded-xl p-3 border border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-1.5 mb-1">
                <Calendar className="w-3.5 h-3.5 text-green-500" />
                <span className="text-sm font-bold text-gray-900">{profile.totalSessions || 0}</span>
              </div>
              <p className="text-[10px] text-gray-500">Sessions</p>
            </div>
            <div className="min-w-[100px] bg-white rounded-xl p-3 border border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-1.5 mb-1">
                <MessageCircle className="w-3.5 h-3.5 text-purple-500" />
                <span className="text-sm font-bold text-gray-900">{profile.reviewsCount || 0}</span>
              </div>
              <p className="text-[10px] text-gray-500">Reviews</p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 mb-4 bg-white border border-gray-200 rounded-xl">
          {[
            { id: "profile", label: "Profile", icon: User },
            { id: "availability", label: "Availability", icon: Calendar },
            { id: "social", label: "Social", icon: Globe },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg transition-all ${
                  activeTab === tab.id
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-500 hover:text-primary hover:bg-orange-50"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Profile Tab Content */}
        {activeTab === "profile" && (
          <div className="space-y-3">
            {/* Basic Info */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-900 uppercase">Basic Information</h2>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block mb-1 text-[10px] font-medium text-gray-500">First Name</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      disabled
                      className="w-full px-3 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg cursor-not-allowed bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-[10px] font-medium text-gray-500">Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      disabled
                      className="w-full px-3 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg cursor-not-allowed bg-gray-50"
                    />
                  </div>
                </div>
                <div>
                  <label className="block mb-1 text-[10px] font-medium text-gray-500">Title / Role</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Senior Software Engineer"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block mb-1 text-[10px] font-medium text-gray-500">Company</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                      placeholder="e.g., Google, Microsoft"
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-[10px] font-medium text-gray-500">Years Experience</label>
                    <input
                      type="number"
                      value={formData.yearsOfExperience}
                      onChange={(e) => setFormData((prev) => ({ ...prev, yearsOfExperience: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-900 uppercase">Bio</h2>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData((prev) => ({ ...prev, bio: e.target.value }))}
                rows={4}
                placeholder="Tell students about yourself, your experience, and how you can help them..."
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <p className="text-[10px] text-gray-400 mt-1">{formData.bio.length}/500 characters</p>
            </div>

            {/* Price */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-900 uppercase">Session Price</h2>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">USD</span>
                  <input
                    type="number"
                    value={formData.pricePerSession}
                    onChange={(e) => setFormData((prev) => ({ ...prev, pricePerSession: parseInt(e.target.value) || 0 }))}
                    className="w-24 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3 text-gray-400" />
                  <span className="text-xs font-medium text-gray-900">{fmtXAF(formData.pricePerSession)}</span>
                  <span className="text-[10px] text-gray-400">(per 60 min session)</span>
                </div>
              </div>
            </div>

            {/* Expertise */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-900 uppercase">Expertise</h2>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {formData.expertise.map((exp) => (
                  <span
                    key={exp}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-orange-50 text-orange-600 rounded-full"
                  >
                    {exp}
                    <button onClick={() => removeExpertise(exp)} className="hover:text-orange-800">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newExpertise}
                  onChange={(e) => setNewExpertise(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExpertise())}
                  placeholder="Add expertise (React, Python, UI/UX...)"
                  className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <button
                  onClick={addExpertise}
                  className="px-3 py-2 text-xs font-medium text-white rounded-lg bg-primary hover:bg-primary/90"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Languages */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-900 uppercase">Languages</h2>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {formData.languages.map((lang) => (
                  <span
                    key={lang}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-blue-50 text-blue-600 rounded-full"
                  >
                    {lang}
                    <button onClick={() => removeLanguage(lang)} className="hover:text-blue-800">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLanguage())}
                  placeholder="Add language (English, French...)"
                  className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <button
                  onClick={addLanguage}
                  className="px-3 py-2 text-xs font-medium text-white rounded-lg bg-primary hover:bg-primary/90"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Availability Tab Content */}
        {activeTab === "availability" && (
          <div className="p-4 bg-white border border-gray-200 rounded-xl">
            <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-900 uppercase">Weekly Schedule</h2>
            
            {/* Existing Availability */}
            <div className="mb-4 space-y-2">
              {availability.length === 0 ? (
                <div className="py-6 text-center rounded-lg bg-gray-50">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs text-gray-400">No availability set yet</p>
                  <p className="text-[10px] text-gray-400">Add your available time slots below</p>
                </div>
              ) : (
                availability.map((slot, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded-lg bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center bg-green-100 rounded-lg w-7 h-7">
                        <Clock className="w-3.5 h-3.5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-900">{DAYS_OF_WEEK[slot.dayOfWeek]}</p>
                        <p className="text-[10px] text-gray-500">{slot.startTime} - {slot.endTime}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeAvailability(idx)}
                      className="p-1.5 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add New Slot */}
            <div className="pt-4 border-t border-gray-200">
              <p className="mb-3 text-xs font-medium text-gray-700">Add Time Slot</p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[120px]">
                  <label className="block mb-1 text-[10px] text-gray-500">Day</label>
                  <select
                    value={newSlot.dayOfWeek}
                    onChange={(e) => setNewSlot((prev) => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {DAYS_OF_WEEK.map((day, idx) => (
                      <option key={idx} value={idx}>{day}</option>
                    ))}
                  </select>
                </div>
                <div className="w-[100px]">
                  <label className="block mb-1 text-[10px] text-gray-500">From</label>
                  <input
                    type="time"
                    value={newSlot.startTime}
                    onChange={(e) => setNewSlot((prev) => ({ ...prev, startTime: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="w-[100px]">
                  <label className="block mb-1 text-[10px] text-gray-500">To</label>
                  <input
                    type="time"
                    value={newSlot.endTime}
                    onChange={(e) => setNewSlot((prev) => ({ ...prev, endTime: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <button
                  onClick={addAvailability}
                  className="px-3 py-2 text-xs font-medium text-white rounded-lg bg-primary hover:bg-primary/90"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Social Tab Content */}
        {activeTab === "social" && (
          <div className="p-4 bg-white border border-gray-200 rounded-xl">
            <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-900 uppercase">Connect Social Profiles</h2>
            <div className="space-y-3">
              {/* LinkedIn */}
              <div>
                <label className="flex items-center gap-1.5 mb-1 text-[10px] font-medium text-gray-500">
                  <Linkedin className="w-3 h-3 text-blue-600" />
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  value={formData.linkedin}
                  onChange={(e) => setFormData((prev) => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="https://linkedin.com/in/username"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Twitter/X */}
              <div>
                <label className="flex items-center gap-1.5 mb-1 text-[10px] font-medium text-gray-500">
                  <Twitter className="w-3 h-3 text-sky-500" />
                  Twitter / X
                </label>
                <input
                  type="text"
                  value={formData.twitter}
                  onChange={(e) => setFormData((prev) => ({ ...prev, twitter: e.target.value }))}
                  placeholder="@username"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Facebook */}
              <div>
                <label className="flex items-center gap-1.5 mb-1 text-[10px] font-medium text-gray-500">
                  <Facebook className="w-3 h-3 text-blue-700" />
                  Facebook
                </label>
                <input
                  type="url"
                  value={formData.facebook}
                  onChange={(e) => setFormData((prev) => ({ ...prev, facebook: e.target.value }))}
                  placeholder="https://facebook.com/username"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Website */}
              <div>
                <label className="flex items-center gap-1.5 mb-1 text-[10px] font-medium text-gray-500">
                  <Globe className="w-3 h-3 text-green-600" />
                  Personal Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
                  placeholder="https://yourwebsite.com"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default MentorProfilePage;